import React, { useEffect, useState } from 'react';
import {
  Form,
  Modal,
  message,
  Input,
  Button,
  Table,
  Spin,
  Pagination,
  Popconfirm,
  Radio,
} from 'antd';
import { connect } from 'react-redux';
import moment from 'moment';
import { useIntl } from 'react-intl';
import {
  actionExportLabel,
  actionGetExportedVersions,
  actionDownloadLabel,
  actionDeleteExportLabel,
} from '../StudyListAction';
import { CONFIG_SERVER } from '../../../utils/constants/config';

let downloadLabelUrl =
  CONFIG_SERVER.BASE_URL + '/api/stats/label_exports/download/';
let params = { _offset: 0, _limit: 10 };
let intervalExport = null;
const EXPORT_STATUS = {
  PENDING: 'PENDING',
  DONE: 'DONE',
  FAILED: 'FAILED',
};

const ExportLabelModal = (props) => {
  const { formatMessage: t } = useIntl();
  const [processing, setProcessing] = useState(false);
  const { visible = true, onCancel, exportedVersion, projectId } = props;
  const [form] = Form.useForm();

  useEffect(() => {
    return () => {
      params = { _offset: 0, _limit: 10 };
      clearInterval(intervalExport);
      intervalExport = null;
    };
  }, []);

  useEffect(() => {
    if (projectId) {
      params = {
        ...params,
        _search: `project_id:${projectId}`,
      };
      handleGetExportedVersion(params);
    }
    // eslint-disable-next-line
  }, [projectId]);

  useEffect(() => {
    if (projectId && exportedVersion?.data) {
      const hasPending = (exportedVersion?.data || []).find(
        (it) => it.status === EXPORT_STATUS.PENDING
      );
      /* Start polling only if (a) something is pending and (b) we don't
       * already have an interval running.  Stop polling only when nothing
       * is pending — and crucially null `intervalExport` after clearing,
       * otherwise the truthy-check would prevent a future restart.
       *
       * The previous logic cleared the interval after the first refetch
       * regardless of whether the record was still PENDING, which worked
       * by accident for sub-second exports but breaks for any pipeline
       * that takes more than ~1s (e.g. COCO + Images, which fetches
       * DICOMs from Orthanc and bundles a zip). */
      if (hasPending) {
        if (!intervalExport) {
          intervalExport = setInterval(() => {
            handleGetExportedVersion(params);
          }, 1000);
        }
      } else if (intervalExport) {
        clearInterval(intervalExport);
        intervalExport = null;
      }
    }
    // eslint-disable-next-line
  }, [projectId, exportedVersion]);

  const handleGetExportedVersion = (newParams = {}) => {
    props.actionGetExportedVersions({
      ...newParams,
      _offset: newParams._offset * newParams._limit,
    });
  };

  const handleOk = (event) => {
    if (processing) return;
    event.stopPropagation();
    form
      .validateFields()
      .then(async (values) => {
        try {
          /* `format` is added to the existing payload — backend accepts the
           * field optionally; absent / "json" produces the simple
           * annotations-only dump, "coco" produces an ML-ready zip (DICOMs
           * as PNG + COCO annotations + raw JSON + manifest). */
          const dataDTO = {
            tag: values.tag.trim(),
            project_id: projectId,
            format: values.format || 'json',
          };
          setProcessing(true);
          await actionExportLabel(dataDTO);
          params._offset = 0;
          handleGetExportedVersion(params);
          form.resetFields(['tag']);
          message.success(
            dataDTO.format === 'coco'
              ? 'Building COCO archive — this can take several minutes for large projects.'
              : 'Creating file!'
          );
          setProcessing(false);
        } catch (error) {
          const { data = {} } = error || {};
          message.error(data.message || 'System error');
          setProcessing(false);
        }
      })
      .catch((error) => {});
  };

  const columns = [
    {
      title: t({ id: 'IDS_VERSION_TAG' }),
      dataIndex: 'tag',
      key: 'tag',
    },
    {
      title: 'Format',
      dataIndex: 'format',
      key: 'format',
      width: 120,
      render: (txt) => {
        const f = (txt || 'json').toLowerCase();
        const label = f === 'coco' ? 'COCO + Images (zip)' : 'Annotations JSON';
        return <span style={{ fontSize: 11, color: '#6b7280' }}>{label}</span>;
      },
    },
    {
      title: t({ id: 'IDS_DATE' }),
      dataIndex: 'created',
      key: 'created',
      render: (txt) => (
        <span>{txt ? moment(txt).format('YYYY-MM-DD HH:mm') : ''}</span>
      ),
    },
    {
      title: t({ id: 'IDS_STATUS' }),
      dataIndex: 'status',
      key: 'status',
      render: (txt, record) => {
        const status = (txt || '').toLocaleLowerCase();
        /* Show the error message inline when the export failed so the
         * user has actionable diagnostics (e.g. "orthanc_not_found"). */
        if (status === 'failed') {
          return (
            <span className={`export-lb-status ${status}`} style={{ color: '#dc2626' }}
                  title={record?.error_message || ''}>
              failed
              {record?.error_message && (
                <div style={{ fontSize: 10, color: '#9ca3af', maxWidth: 180,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {record.error_message}
                </div>
              )}
            </span>
          );
        }
        return <span className={`export-lb-status ${status}`}>{status}</span>;
      },
    },
    {
      title: t({ id: 'IDS_ACTION' }),
      width: 180,
      align: 'center',
      dataIndex: 'action',
      key: 'action',
      render: (_, record) => (
        <span style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <Button
            type="link"
            disabled={record?.status === EXPORT_STATUS.PENDING || record?.status === EXPORT_STATUS.FAILED}
            onClick={() => {
              actionDownloadLabel(downloadLabelUrl + record?.id);
            }}
          >
            {t({ id: 'IDS_DOWNLOAD' })}
          </Button>
          <Popconfirm
            title="Delete this version tag?"
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
            onConfirm={async () => {
              try {
                await actionDeleteExportLabel(record?.id);
                handleGetExportedVersion(params);
              } catch {
                message.error('Failed to delete version');
              }
            }}
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </span>
      ),
    },
  ];

  const onChangePagination = (page, size) => {
    params = { ...params, _offset: page - 1, _limit: size };
    handleGetExportedVersion(params);
  };

  return (
    <Modal
      title={t({ id: 'IDS_EXPORT_LABEL' })}
      visible={visible}
      className="common-modal"
      onCancel={onCancel}
      width={900}
      maskClosable={false}
      footer={[
        <Button key="submit" type="primary" onClick={handleOk}>
          {t({ id: 'IDS_EXPORT' })}
        </Button>,
      ]}
    >
      <Spin spinning={processing}>
        <div className="export-label-modal">
          <div className="exported-version">
            <div className="title">{t({ id: 'IDS_EXPORTED_VERSIONS' })}</div>
            <Table
              size="small"
              scroll={{ y: 250 }}
              rowKey={(record) => record.id}
              className="exported-table"
              dataSource={exportedVersion?.data || []}
              columns={columns}
              pagination={false}
            />
            <Pagination
              size="small"
              className="pagination-content"
              total={exportedVersion?.count || 0}
              current={params._offset + 1}
              pageSize={params._limit}
              showSizeChanger
              showLessItems={true}
              pageSizeOptions={[10, 20, 50]}
              onChange={onChangePagination}
            />
          </div>
          <div className="form-content">
            <div className="title">{t({ id: 'IDS_EXPORT_NEW_VERSION' })}</div>
            <Form
              form={form}
              name="exportLabel"
              initialValues={{ format: 'json' }}
            >
              <Form.Item
                name="tag"
                label={t({ id: 'IDS_TAG' })}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                  },
                  {
                    max: 255,
                    pattern: /^[\w.]+$/,
                  },
                ]}
                labelAlign="left"
              >
                <Input />
              </Form.Item>
              {/* Format chooser — Annotations JSON keeps the simple
                  single-file output (annotations only); COCO + Images
                  produces a zip with DICOMs rendered as PNG + COCO
                  annotations + raw JSON + manifest, ready for ML
                  training pipelines. */}
              <Form.Item
                name="format"
                label="Format"
                labelAlign="left"
                rules={[{ required: true }]}
              >
                <Radio.Group>
                  <Radio value="json" style={{ display: 'block', marginBottom: 4 }}>
                    <strong>Annotations JSON</strong>
                    <div style={{ fontSize: 11, color: '#6b7280', marginLeft: 24 }}>
                      Annotations only. Single .json file. Fast.
                    </div>
                  </Radio>
                  <Radio value="coco" style={{ display: 'block' }}>
                    <strong>COCO + Images (zip)</strong>
                    <div style={{ fontSize: 11, color: '#6b7280', marginLeft: 24 }}>
                      DICOMs rendered as PNG + COCO annotations + masks +
                      raw JSON + manifest. Ready for ML training. Slower —
                      builds in the background; status updates above.
                    </div>
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Form>
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default connect(
  (state) => ({
    exportedVersion: state.study.exportedVersion,
    isFetching: state.study.isFetching,
  }),
  { actionGetExportedVersions }
)(ExportLabelModal);
